/**
 * The racquet shop's own settings.
 *
 * Four things are drawn rather than explained:
 *
 *   A club says how many courts it has, once. The first thing anybody
 *   does when setting this up is type "we have six", and the second
 *   thing they do is give up halfway through six identical forms. So the
 *   form takes a count, and a club whose courts have names of their own
 *   can still name one.
 *
 *   Shared ground is a first-class thing on the courts tab and not a
 *   setting buried inside a court. It is the fact that makes this module
 *   different from the tee sheet, and a club that forgets to record it
 *   sends two groups to one piece of asphalt.
 *
 *   The rules are shown per membership category, with the categories
 *   that have no rules listed too. A category with no privileges row has
 *   not been given rules and is not guessed at — but the committee
 *   should be able to SEE that it has not, which is why the empty ones
 *   are on the screen rather than absent from it.
 *
 *   A season change does not relay a sheet already opened for those
 *   dates. The API says so and it is repeated here, because the
 *   alternative is a professional discovering it on Saturday.
 */

import { useState, type ReactNode } from 'react';
import { api } from '../lib/api.ts';
import { useApi, useMutation } from '../lib/useApi.ts';
import { useSession } from '../lib/session.tsx';
import { Card, Empty, ErrorBanner, Loading, Status } from '../components/ui.tsx';
import { date as fmtDate, dateTime } from '../lib/format.ts';

/* ------------------------------------------------------------------ */
/* Shapes                                                              */
/* ------------------------------------------------------------------ */

interface Court {
  id: string;
  code: string;
  name: string;
  sport: string;
  surface: string;
  enclosure: string;
  has_lights: boolean;
  outlet_name: string | null;
  is_active: boolean;
  is_default: boolean;
  sort_order: number;
  notes: string | null;
  shares_ground_with: string[];
}

interface Season {
  id: string;
  name: string;
  starts_on: string;
  ends_on: string;
  slot_minutes: number;
  first_start_at: string;
  last_start_at: string;
  last_start_at_unlit: string | null;
  max_players_per_slot: number;
  note: string | null;
}

interface Holiday {
  id: string;
  holiday_on: string;
  name: string;
}

interface Privilege {
  category_id: string;
  category_code: string;
  category_name: string;
  id: string | null;
  may_book: boolean | null;
  advance_days: number | null;
  weekend_earliest_start: string | null;
  weekday_earliest_start: string | null;
  max_open_reservations: number | null;
  prime_time_starts_at: string | null;
  prime_time_ends_at: string | null;
  max_prime_time_per_week: number | null;
  cancel_cutoff_hours: number | null;
  max_guests_per_reservation: number | null;
}

interface Professional {
  id: string;
  person_id: string;
  name: string;
  title: string;
  teaches: string;
  rate_class: string;
  is_active: boolean;
}

interface Lesson {
  lesson_id: string;
  professional_name: string;
  court_name: string | null;
  student_name: string | null;
  starts_at: string;
  duration_minutes: number;
  kind: string;
  status: string;
}

interface Equipment {
  resource_id: string;
  code: string;
  name: string;
  kind: string;
  status: string;
  out_from: string | null;
  due_back: string | null;
  out_to: string | null;
}

type Tab = 'courts' | 'seasons' | 'rules' | 'lessons' | 'equipment';

const TABS: { id: Tab; label: string }[] = [
  { id: 'courts', label: 'Courts' },
  { id: 'seasons', label: 'Hours & seasons' },
  { id: 'rules', label: 'Booking rules' },
  { id: 'lessons', label: 'Lesson book' },
  { id: 'equipment', label: 'Equipment' },
];

/** Postgres hands back 09:00:00; a form says 09:00. */
const hhmm = (t: string | null) => (t ? t.slice(0, 5) : '');
const today = () => new Date().toISOString().slice(0, 10);

const SURFACES = [
  ['hard', 'Hard'],
  ['clay', 'Clay'],
  ['har_tru', 'Har-Tru'],
  ['grass', 'Grass'],
  ['carpet', 'Carpet'],
  ['synthetic', 'Synthetic'],
];

/* ------------------------------------------------------------------ */
/* Shared bits                                                         */
/* ------------------------------------------------------------------ */

function Actions({ children }: { children: ReactNode }) {
  return <div className="actions">{children}</div>;
}

/**
 * Two clicks, and the second one is labelled with what it does rather
 * than "OK" — a row of identical confirm dialogs is a row of things
 * people stop reading.
 */
function ConfirmButton({
  label,
  confirmLabel,
  onConfirm,
  busy,
}: {
  label: string;
  confirmLabel: string;
  onConfirm: () => void;
  busy?: boolean;
}) {
  const [armed, setArmed] = useState(false);
  if (!armed) {
    return (
      <button className="secondary small" onClick={() => setArmed(true)}>
        {label}
      </button>
    );
  }
  return (
    <>
      <button className="danger small" disabled={busy} onClick={onConfirm}>
        {busy ? 'Working…' : confirmLabel}
      </button>
      <button className="secondary small" onClick={() => setArmed(false)}>
        Keep
      </button>
    </>
  );
}

/* ------------------------------------------------------------------ */
/* The page                                                            */
/* ------------------------------------------------------------------ */

export function TennisAdminPage() {
  const { atLeast } = useSession();
  const canEdit = atLeast('manager');
  const [tab, setTab] = useState<Tab>('courts');

  return (
    <>
      <div className="row" style={{ marginBottom: 16 }}>
        <div>
          <h1>Racquet Sports</h1>
          <div className="muted small">
            {canEdit
              ? 'The courts, the hours, and the rules the committee voted on'
              : 'Read only — a manager changes these'}
          </div>
        </div>
      </div>

      <div className="actions" style={{ marginBottom: 14 }}>
        {TABS.map((t) => (
          <button
            key={t.id}
            className={tab === t.id ? 'gold small' : 'secondary small'}
            onClick={() => setTab(t.id)}
          >
            {t.label}
          </button>
        ))}
      </div>

      {tab === 'courts' && <Courts canEdit={canEdit} />}
      {tab === 'seasons' && <Seasons canEdit={canEdit} />}
      {tab === 'rules' && <Rules canEdit={canEdit} />}
      {tab === 'lessons' && <Lessons canEdit={canEdit} />}
      {tab === 'equipment' && <EquipmentTab canEdit={canEdit} />}
    </>
  );
}

/* ------------------------------------------------------------------ */
/* Courts                                                              */
/* ------------------------------------------------------------------ */

function Courts({ canEdit }: { canEdit: boolean }) {
  const courts = useApi<{ courts: Court[] }>('/v1/tennis/courts?include_inactive=true');
  const [adding, setAdding] = useState(false);
  const [linking, setLinking] = useState<Court | null>(null);
  const [note, setNote] = useState<string | null>(null);

  const retire = useMutation(async (id: string) => {
    const result = await api<{ future_reservations: number }>(`/v1/tennis/courts/${id}`, {
      method: 'DELETE',
    });
    setNote(
      result.future_reservations > 0
        ? `Taken out of service. ${result.future_reservations} reservation(s) are still ` +
          'standing on it — somebody has to telephone those members.'
        : 'Taken out of service. Nothing was booked on it.',
    );
    courts.reload();
  });

  const rows = courts.data?.courts ?? [];

  return (
    <>
      <ErrorBanner error={courts.error} />
      <ErrorBanner error={retire.error} />

      {note && (
        <Card>
          <Status tone="good">{note}</Status>
        </Card>
      )}

      {adding && (
        <AddCourtsForm
          onDone={(message) => {
            setAdding(false);
            setNote(message);
            courts.reload();
          }}
          onCancel={() => setAdding(false)}
        />
      )}

      {linking && (
        <ShareGroundForm
          court={linking}
          courts={rows}
          onDone={(message) => {
            setLinking(null);
            setNote(message);
            courts.reload();
          }}
          onCancel={() => setLinking(null)}
        />
      )}

      <Card
        title="Courts"
        actions={
          canEdit && (
            <Actions>
              <button className="small" onClick={() => setAdding(true)}>
                Add courts
              </button>
            </Actions>
          )
        }
        flush
      >
        {courts.loading ? (
          <div style={{ padding: 16 }}>
            <Loading rows={5} />
          </div>
        ) : rows.length === 0 ? (
          <Empty>
            No courts yet. Adding them is one form — say how many and what they are made of.
          </Empty>
        ) : (
          <table className="data">
            <thead>
              <tr>
                <th style={{ width: 80 }}>Code</th>
                <th>Court</th>
                <th style={{ width: 110 }}>Sport</th>
                <th style={{ width: 160 }}>Made of</th>
                <th>Shares ground with</th>
                <th style={{ width: 210 }} />
              </tr>
            </thead>
            <tbody>
              {rows.map((c) => (
                <tr key={c.id}>
                  <td className="mono">{c.code}</td>
                  <td>
                    {c.name}
                    {!c.is_active && <span className="muted small"> · out of service</span>}
                  </td>
                  <td className="muted small">{c.sport}</td>
                  <td className="muted small">
                    {SURFACES.find(([v]) => v === c.surface)?.[1] ?? c.surface}
                    {c.enclosure !== 'outdoor' ? ` · ${c.enclosure}` : ''}
                    {c.has_lights ? ' · lights' : ''}
                  </td>
                  <td>
                    {c.shares_ground_with.length === 0 ? (
                      <span className="muted small">—</span>
                    ) : (
                      <Status tone="warning">{c.shares_ground_with.join(', ')}</Status>
                    )}
                  </td>
                  <td>
                    {canEdit && (
                      <Actions>
                        <button className="secondary small" onClick={() => setLinking(c)}>
                          Shared ground
                        </button>
                        {c.is_active && (
                          <ConfirmButton
                            label="Retire"
                            confirmLabel={`Take ${c.name} out of service`}
                            busy={retire.busy}
                            onConfirm={() => retire.run(c.id)}
                          />
                        )}
                      </Actions>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </Card>

      <div className="hint" style={{ marginTop: 10 }}>
        A court is a bookable line-out, not a slab. Pickleball courts painted on a tennis court
        are their own courts here, joined to it as shared ground — which is what stops the desk
        selling both at once.
      </div>
    </>
  );
}

function AddCourtsForm({
  onDone,
  onCancel,
}: {
  onDone: (message: string) => void;
  onCancel: () => void;
}) {
  const [mode, setMode] = useState<'many' | 'one'>('many');
  const [f, setF] = useState({
    count: '6',
    name_prefix: 'Court',
    code_prefix: 'CT',
    start_number: '1',
    code: '',
    name: '',
    sport: 'tennis',
    surface: 'hard',
    enclosure: 'outdoor',
    has_lights: false,
  });

  const add = useMutation(async () => {
    const shared = {
      sport: f.sport,
      surface: f.surface,
      enclosure: f.enclosure,
      has_lights: f.has_lights,
    };
    const result = await api<{ courts: { code: string }[] }>('/v1/tennis/courts', {
      method: 'POST',
      body:
        mode === 'many'
          ? {
              ...shared,
              count: Number(f.count),
              name_prefix: f.name_prefix,
              code_prefix: f.code_prefix,
              start_number: Number(f.start_number),
            }
          : { ...shared, code: f.code.toUpperCase(), name: f.name },
    });
    onDone(
      `Added ${result.courts.length} court(s): ${result.courts.map((c) => c.code).join(', ')}.`,
    );
  });

  return (
    <Card title="Add Courts">
      <ErrorBanner error={add.error} />

      <Actions>
        <button
          className={mode === 'many' ? 'gold small' : 'secondary small'}
          onClick={() => setMode('many')}
        >
          A numbered run
        </button>
        <button
          className={mode === 'one' ? 'gold small' : 'secondary small'}
          onClick={() => setMode('one')}
        >
          One with its own name
        </button>
      </Actions>

      <form
        onSubmit={(e) => {
          e.preventDefault();
          add.run();
        }}
        style={{ marginTop: 12 }}
      >
        {mode === 'many' ? (
          <div className="grid-2">
            <label className="field">
              <span>How many</span>
              <input
                type="number"
                min={1}
                max={40}
                value={f.count}
                onChange={(e) => setF({ ...f, count: e.target.value })}
              />
            </label>
            <label className="field">
              <span>Starting at number</span>
              <input
                type="number"
                min={1}
                value={f.start_number}
                onChange={(e) => setF({ ...f, start_number: e.target.value })}
              />
            </label>
            <label className="field">
              <span>Called</span>
              <input
                value={f.name_prefix}
                onChange={(e) => setF({ ...f, name_prefix: e.target.value })}
                placeholder="Court"
              />
            </label>
            <label className="field">
              <span>Short code</span>
              <input
                value={f.code_prefix}
                onChange={(e) => setF({ ...f, code_prefix: e.target.value.toUpperCase() })}
                placeholder="CT"
              />
            </label>
          </div>
        ) : (
          <div className="grid-2">
            <label className="field">
              <span>Short code</span>
              <input
                value={f.code}
                onChange={(e) => setF({ ...f, code: e.target.value.toUpperCase() })}
                placeholder="STADIUM"
              />
            </label>
            <label className="field">
              <span>Name</span>
              <input
                value={f.name}
                onChange={(e) => setF({ ...f, name: e.target.value })}
                placeholder="The Stadium Court"
              />
            </label>
          </div>
        )}

        <div className="grid-2">
          <label className="field">
            <span>Sport</span>
            <select value={f.sport} onChange={(e) => setF({ ...f, sport: e.target.value })}>
              <option value="tennis">Tennis</option>
              <option value="pickleball">Pickleball</option>
            </select>
          </label>
          <label className="field">
            <span>Surface</span>
            <select value={f.surface} onChange={(e) => setF({ ...f, surface: e.target.value })}>
              {SURFACES.map(([value, label]) => (
                <option key={value} value={value}>
                  {label}
                </option>
              ))}
            </select>
          </label>
          <label className="field">
            <span>Indoors or out</span>
            <select
              value={f.enclosure}
              onChange={(e) => setF({ ...f, enclosure: e.target.value })}
            >
              <option value="outdoor">Outdoor</option>
              <option value="covered">Covered</option>
              <option value="indoor">Indoor</option>
            </select>
          </label>
          <label
            className="muted small"
            style={{ display: 'flex', alignItems: 'center', gap: 6, marginTop: 22 }}
          >
            <input
              type="checkbox"
              checked={f.has_lights}
              onChange={(e) => setF({ ...f, has_lights: e.target.checked })}
            />
            These courts have lights
          </label>
        </div>

        <div className="hint">
          Lights are not decoration: an unlit court stops selling when the season says an unlit
          court stops, which is four months of evenings.
        </div>

        <Actions>
          <button className="gold" type="submit" disabled={add.busy}>
            {add.busy ? 'Adding…' : 'Add them'}
          </button>
          <button className="secondary" type="button" onClick={onCancel}>
            Cancel
          </button>
        </Actions>
      </form>
    </Card>
  );
}

function ShareGroundForm({
  court,
  courts,
  onDone,
  onCancel,
}: {
  court: Court;
  courts: Court[];
  onDone: (message: string) => void;
  onCancel: () => void;
}) {
  const [otherId, setOtherId] = useState('');

  const link = useMutation(async () => {
    const other = courts.find((c) => c.id === otherId);
    await api(`/v1/tennis/courts/${court.id}/overlaps`, {
      method: 'POST',
      body: { other_court_id: otherId },
    });
    onDone(
      `${court.name} and ${other?.name ?? 'that court'} are now the same ground — booking ` +
        'either one takes the other off the sheet.',
    );
  });

  const candidates = courts.filter(
    (c) => c.id !== court.id && !court.shares_ground_with.includes(c.name),
  );

  return (
    <Card title={`Ground shared with ${court.name}`}>
      <ErrorBanner error={link.error} />

      {court.shares_ground_with.length > 0 && (
        <p className="small">
          Already the same ground as <strong>{court.shares_ground_with.join(', ')}</strong>.
        </p>
      )}

      <label className="field">
        <span>Also the same ground as</span>
        <select value={otherId} onChange={(e) => setOtherId(e.target.value)}>
          <option value="">Choose a court</option>
          {candidates.map((c) => (
            <option key={c.id} value={c.id}>
              {c.name} ({c.sport})
            </option>
          ))}
        </select>
      </label>

      <div className="hint">
        Say this for each pair that physically overlaps, not for a whole bank of courts. Four
        pickleball courts painted on one tennis court each share ground with the tennis court and
        with none of each other — which is exactly what lets all four play at once.
      </div>

      <Actions>
        <button className="gold" disabled={!otherId || link.busy} onClick={() => link.run()}>
          {link.busy ? 'Saving…' : 'Same ground'}
        </button>
        <button className="secondary" onClick={onCancel}>
          Close
        </button>
      </Actions>
    </Card>
  );
}

/* ------------------------------------------------------------------ */
/* Hours & seasons                                                     */
/* ------------------------------------------------------------------ */

const BLANK_SEASON = {
  name: '',
  starts_on: '',
  ends_on: '',
  slot_minutes: '60',
  first_start_at: '07:00',
  last_start_at: '20:00',
  last_start_at_unlit: '',
};

function Seasons({ canEdit }: { canEdit: boolean }) {
  const data = useApi<{ seasons: Season[]; holidays: Holiday[] }>('/v1/tennis/seasons');
  const [adding, setAdding] = useState(false);
  const [note, setNote] = useState<string | null>(null);
  const [f, setF] = useState(BLANK_SEASON);
  const [holiday, setHoliday] = useState({ holiday_on: '', name: '' });

  const save = useMutation(async () => {
    await api('/v1/tennis/seasons', {
      method: 'POST',
      body: {
        name: f.name,
        starts_on: f.starts_on,
        ends_on: f.ends_on,
        slot_minutes: Number(f.slot_minutes),
        first_start_at: f.first_start_at,
        last_start_at: f.last_start_at,
        last_start_at_unlit: f.last_start_at_unlit || null,
      },
    });
    setAdding(false);
    setF(BLANK_SEASON);
    setNote('Saved. Days already opened keep the shape they were opened with.');
    data.reload();
  });

  const remove = useMutation(async (id: string) => {
    await api(`/v1/tennis/seasons/${id}`, { method: 'DELETE' });
    data.reload();
  });

  const addHoliday = useMutation(async () => {
    await api('/v1/tennis/holidays', { method: 'POST', body: holiday });
    setHoliday({ holiday_on: '', name: '' });
    data.reload();
  });

  const seasons = data.data?.seasons ?? [];
  const holidays = data.data?.holidays ?? [];

  return (
    <>
      <ErrorBanner error={data.error} />
      <ErrorBanner error={save.error} />
      <ErrorBanner error={remove.error} />
      <ErrorBanner error={addHoliday.error} />

      {note && (
        <Card>
          <Status tone="good">{note}</Status>
        </Card>
      )}

      <Card
        title="Hours & Seasons"
        actions={
          canEdit && (
            <Actions>
              <button className="small" onClick={() => setAdding(!adding)}>
                {adding ? 'Close' : 'Add a season'}
              </button>
            </Actions>
          )
        }
        flush
      >
        {adding && (
          <form
            style={{ padding: 16 }}
            onSubmit={(e) => {
              e.preventDefault();
              save.run();
            }}
          >
            <div className="grid-2">
              <label className="field">
                <span>Name</span>
                <input
                  value={f.name}
                  onChange={(e) => setF({ ...f, name: e.target.value })}
                  placeholder="Summer"
                />
              </label>
              <label className="field">
                <span>How long a court is booked for</span>
                <select
                  value={f.slot_minutes}
                  onChange={(e) => setF({ ...f, slot_minutes: e.target.value })}
                >
                  <option value="30">30 minutes</option>
                  <option value="60">60 minutes</option>
                  <option value="90">90 minutes</option>
                  <option value="120">120 minutes</option>
                </select>
              </label>
              <label className="field">
                <span>From</span>
                <input
                  type="date"
                  value={f.starts_on}
                  onChange={(e) => setF({ ...f, starts_on: e.target.value })}
                />
              </label>
              <label className="field">
                <span>Through</span>
                <input
                  type="date"
                  value={f.ends_on}
                  onChange={(e) => setF({ ...f, ends_on: e.target.value })}
                />
              </label>
              <label className="field">
                <span>First court of the day</span>
                <input
                  type="time"
                  value={f.first_start_at}
                  onChange={(e) => setF({ ...f, first_start_at: e.target.value })}
                />
              </label>
              <label className="field">
                <span>Last court of the day</span>
                <input
                  type="time"
                  value={f.last_start_at}
                  onChange={(e) => setF({ ...f, last_start_at: e.target.value })}
                />
              </label>
              <label className="field">
                <span>Last court on a court with no lights</span>
                <input
                  type="time"
                  value={f.last_start_at_unlit}
                  onChange={(e) => setF({ ...f, last_start_at_unlit: e.target.value })}
                />
              </label>
            </div>

            <div className="hint">
              Leave the unlit time empty if the club sells every court to the same hour. Seasons
              cannot overlap: two seasons on one day means the sheet has no answer for how long a
              court is booked for.
            </div>

            <Actions>
              <button className="gold" type="submit" disabled={save.busy}>
                {save.busy ? 'Saving…' : 'Save the season'}
              </button>
            </Actions>
          </form>
        )}

        {data.loading ? (
          <div style={{ padding: 16 }}>
            <Loading rows={3} />
          </div>
        ) : seasons.length === 0 ? (
          <Empty>
            No season yet, so the court sheet cannot be opened. A season is what says how long a
            court is booked for and when the first and last one go out.
          </Empty>
        ) : (
          <table className="data">
            <thead>
              <tr>
                <th>Season</th>
                <th style={{ width: 220 }}>Dates</th>
                <th style={{ width: 110 }}>Block</th>
                <th style={{ width: 200 }}>Hours</th>
                <th style={{ width: 150 }} />
              </tr>
            </thead>
            <tbody>
              {seasons.map((s) => (
                <tr key={s.id}>
                  <td>{s.name}</td>
                  <td className="muted small">
                    {fmtDate(s.starts_on)} — {fmtDate(s.ends_on)}
                  </td>
                  <td className="num">{s.slot_minutes} min</td>
                  <td className="muted small">
                    {hhmm(s.first_start_at)}–{hhmm(s.last_start_at)}
                    {s.last_start_at_unlit
                      ? ` · unlit to ${hhmm(s.last_start_at_unlit)}`
                      : ''}
                  </td>
                  <td>
                    {canEdit && (
                      <ConfirmButton
                        label="Remove"
                        confirmLabel={`Remove ${s.name}`}
                        busy={remove.busy}
                        onConfirm={() => remove.run(s.id)}
                      />
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </Card>

      <Card title="Days the Courts Price as a Weekend">
        {canEdit && (
          <div className="row" style={{ marginBottom: 10 }}>
            <input
              type="date"
              value={holiday.holiday_on}
              onChange={(e) => setHoliday({ ...holiday, holiday_on: e.target.value })}
              style={{ maxWidth: 180 }}
            />
            <input
              value={holiday.name}
              onChange={(e) => setHoliday({ ...holiday, name: e.target.value })}
              placeholder="What it is"
              style={{ maxWidth: 260 }}
            />
            <button
              className="secondary small"
              disabled={!holiday.holiday_on || !holiday.name || addHoliday.busy}
              onClick={() => addHoliday.run()}
            >
              Add
            </button>
          </div>
        )}

        {holidays.length === 0 ? (
          <Empty>Nothing listed. Saturdays and Sundays are already weekends.</Empty>
        ) : (
          <ul className="small" style={{ margin: 0, paddingLeft: 18 }}>
            {holidays.map((h) => (
              <li key={h.id}>
                {fmtDate(h.holiday_on)} — {h.name}
              </li>
            ))}
          </ul>
        )}

        <div className="hint">
          This is the club&apos;s own list, kept apart from golf&apos;s: a club may close the
          course for the member-guest and leave the courts open.
        </div>
      </Card>
    </>
  );
}

/* ------------------------------------------------------------------ */
/* Booking rules                                                       */
/* ------------------------------------------------------------------ */

function Rules({ canEdit }: { canEdit: boolean }) {
  const data = useApi<{ privileges: Privilege[] }>('/v1/tennis/privileges');
  const [editing, setEditing] = useState<Privilege | null>(null);
  const [note, setNote] = useState<string | null>(null);

  const rows = data.data?.privileges ?? [];

  return (
    <>
      <ErrorBanner error={data.error} />

      {note && (
        <Card>
          <Status tone="good">{note}</Status>
        </Card>
      )}

      {editing && (
        <RulesForm
          privilege={editing}
          onDone={(message) => {
            setEditing(null);
            setNote(message);
            data.reload();
          }}
          onCancel={() => setEditing(null)}
        />
      )}

      <Card title="What Each Membership May Do on the Courts" flush>
        {data.loading ? (
          <div style={{ padding: 16 }}>
            <Loading rows={4} />
          </div>
        ) : (
          <table className="data">
            <thead>
              <tr>
                <th>Category</th>
                <th style={{ width: 110 }}>Books ahead</th>
                <th style={{ width: 170 }}>Not before</th>
                <th style={{ width: 110 }}>At once</th>
                <th style={{ width: 190 }}>Prime time</th>
                <th style={{ width: 120 }} />
              </tr>
            </thead>
            <tbody>
              {rows.map((p) => (
                <tr key={p.category_id}>
                  <td>
                    {p.category_name}
                    <span className="muted small"> · {p.category_code}</span>
                  </td>
                  {p.id === null ? (
                    <td colSpan={4}>
                      <Status tone="warning">
                        No rules set. Nothing is enforced for this category, and nothing is
                        assumed for it either.
                      </Status>
                    </td>
                  ) : !p.may_book ? (
                    <td colSpan={4}>
                      <Status tone="neutral">Does not book courts</Status>
                    </td>
                  ) : (
                    <>
                      <td className="num">{p.advance_days} days</td>
                      <td className="muted small">
                        {p.weekday_earliest_start || p.weekend_earliest_start
                          ? `${hhmm(p.weekday_earliest_start) || 'any'} weekday · ${
                              hhmm(p.weekend_earliest_start) || 'any'
                            } weekend`
                          : 'any hour'}
                      </td>
                      <td className="num">
                        {p.max_open_reservations === null ? 'no cap' : p.max_open_reservations}
                      </td>
                      <td className="muted small">
                        {p.max_prime_time_per_week === null
                          ? 'no cap'
                          : `${p.max_prime_time_per_week} a week, ${hhmm(
                              p.prime_time_starts_at,
                            )}–${hhmm(p.prime_time_ends_at)}`}
                      </td>
                    </>
                  )}
                  <td>
                    {canEdit && (
                      <button className="secondary small" onClick={() => setEditing(p)}>
                        {p.id === null ? 'Set rules' : 'Change'}
                      </button>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </Card>

      <div className="hint" style={{ marginTop: 10 }}>
        A cap on how many courts a membership holds at once, and on how much of the week&apos;s
        prime time it takes, is the only rationing that works. Every club without one has three
        families who have worked that out.
      </div>
    </>
  );
}

function RulesForm({
  privilege,
  onDone,
  onCancel,
}: {
  privilege: Privilege;
  onDone: (message: string) => void;
  onCancel: () => void;
}) {
  const [f, setF] = useState({
    may_book: privilege.may_book ?? true,
    advance_days: String(privilege.advance_days ?? 7),
    weekday_earliest_start: hhmm(privilege.weekday_earliest_start),
    weekend_earliest_start: hhmm(privilege.weekend_earliest_start),
    max_open_reservations:
      privilege.max_open_reservations === null ? '' : String(privilege.max_open_reservations),
    prime_time_starts_at: hhmm(privilege.prime_time_starts_at),
    prime_time_ends_at: hhmm(privilege.prime_time_ends_at),
    max_prime_time_per_week:
      privilege.max_prime_time_per_week === null
        ? ''
        : String(privilege.max_prime_time_per_week),
    cancel_cutoff_hours: String(privilege.cancel_cutoff_hours ?? 2),
    max_guests_per_reservation:
      privilege.max_guests_per_reservation === null
        ? ''
        : String(privilege.max_guests_per_reservation),
  });

  const save = useMutation(async () => {
    await api(`/v1/tennis/privileges/${privilege.category_id}`, {
      method: 'PUT',
      body: {
        may_book: f.may_book,
        advance_days: Number(f.advance_days),
        weekday_earliest_start: f.weekday_earliest_start || null,
        weekend_earliest_start: f.weekend_earliest_start || null,
        max_open_reservations:
          f.max_open_reservations === '' ? null : Number(f.max_open_reservations),
        prime_time_starts_at: f.prime_time_starts_at || null,
        prime_time_ends_at: f.prime_time_ends_at || null,
        max_prime_time_per_week:
          f.max_prime_time_per_week === '' ? null : Number(f.max_prime_time_per_week),
        cancel_cutoff_hours: Number(f.cancel_cutoff_hours),
        max_guests_per_reservation:
          f.max_guests_per_reservation === '' ? null : Number(f.max_guests_per_reservation),
      },
    });
    onDone(`${privilege.category_name} rules saved.`);
  });

  return (
    <Card title={`${privilege.category_name} on the courts`}>
      <ErrorBanner error={save.error} />

      <form
        onSubmit={(e) => {
          e.preventDefault();
          save.run();
        }}
      >
        <label
          className="muted small"
          style={{ display: 'flex', alignItems: 'center', gap: 6, marginBottom: 12 }}
        >
          <input
            type="checkbox"
            checked={f.may_book}
            onChange={(e) => setF({ ...f, may_book: e.target.checked })}
          />
          This category books courts
        </label>

        {f.may_book && (
          <>
            <div className="grid-2">
              <label className="field">
                <span>Books this many days ahead</span>
                <input
                  type="number"
                  min={0}
                  max={365}
                  value={f.advance_days}
                  onChange={(e) => setF({ ...f, advance_days: e.target.value })}
                />
              </label>
              <label className="field">
                <span>Holds this many courts at once</span>
                <input
                  type="number"
                  min={0}
                  value={f.max_open_reservations}
                  onChange={(e) => setF({ ...f, max_open_reservations: e.target.value })}
                  placeholder="leave empty for no cap"
                />
              </label>
              <label className="field">
                <span>Not before, on a weekday</span>
                <input
                  type="time"
                  value={f.weekday_earliest_start}
                  onChange={(e) => setF({ ...f, weekday_earliest_start: e.target.value })}
                />
              </label>
              <label className="field">
                <span>Not before, at a weekend</span>
                <input
                  type="time"
                  value={f.weekend_earliest_start}
                  onChange={(e) => setF({ ...f, weekend_earliest_start: e.target.value })}
                />
              </label>
            </div>

            <div className="grid-2">
              <label className="field">
                <span>Prime time starts</span>
                <input
                  type="time"
                  value={f.prime_time_starts_at}
                  onChange={(e) => setF({ ...f, prime_time_starts_at: e.target.value })}
                />
              </label>
              <label className="field">
                <span>Prime time ends</span>
                <input
                  type="time"
                  value={f.prime_time_ends_at}
                  onChange={(e) => setF({ ...f, prime_time_ends_at: e.target.value })}
                />
              </label>
              <label className="field">
                <span>Prime-time courts a week</span>
                <input
                  type="number"
                  min={0}
                  value={f.max_prime_time_per_week}
                  onChange={(e) => setF({ ...f, max_prime_time_per_week: e.target.value })}
                  placeholder="leave empty for no cap"
                />
              </label>
              <label className="field">
                <span>Guests on one court</span>
                <input
                  type="number"
                  min={0}
                  max={7}
                  value={f.max_guests_per_reservation}
                  onChange={(e) => setF({ ...f, max_guests_per_reservation: e.target.value })}
                  placeholder="leave empty for no cap"
                />
              </label>
              <label className="field">
                <span>A cancellation is late inside</span>
                <input
                  type="number"
                  min={0}
                  max={168}
                  value={f.cancel_cutoff_hours}
                  onChange={(e) => setF({ ...f, cancel_cutoff_hours: e.target.value })}
                />
              </label>
            </div>

            <div className="hint">
              Prime time is whatever this club says it is — mornings at one club, evenings at
              another. A late cancellation is recorded, never refused: refusing one leaves a
              court held by somebody who is not coming.
            </div>
          </>
        )}

        <Actions>
          <button className="gold" type="submit" disabled={save.busy}>
            {save.busy ? 'Saving…' : 'Save the rules'}
          </button>
          <button className="secondary" type="button" onClick={onCancel}>
            Cancel
          </button>
        </Actions>
      </form>
    </Card>
  );
}

/* ------------------------------------------------------------------ */
/* The lesson book                                                     */
/* ------------------------------------------------------------------ */

function Lessons({ canEdit }: { canEdit: boolean }) {
  const [from, setFrom] = useState(today());
  const pros = useApi<{ professionals: Professional[] }>(
    '/v1/tennis/professionals?include_inactive=true',
  );
  const diary = useApi<{ lessons: Lesson[] }>(
    `/v1/tennis/lessons?from=${from}&through=${from}`,
    [from],
  );

  const lessons = diary.data?.lessons ?? [];

  return (
    <>
      <ErrorBanner error={pros.error} />
      <ErrorBanner error={diary.error} />

      <Card title="Who Teaches" flush>
        {pros.loading ? (
          <div style={{ padding: 16 }}>
            <Loading rows={2} />
          </div>
        ) : (pros.data?.professionals ?? []).length === 0 ? (
          <Empty>
            Nobody yet. A professional is a person the club already has a record for.
          </Empty>
        ) : (
          <table className="data">
            <thead>
              <tr>
                <th>Name</th>
                <th style={{ width: 200 }}>Title</th>
                <th style={{ width: 140 }}>Teaches</th>
                <th style={{ width: 140 }}>Priced as</th>
              </tr>
            </thead>
            <tbody>
              {(pros.data?.professionals ?? []).map((p) => (
                <tr key={p.id}>
                  <td>
                    {p.name}
                    {!p.is_active && <span className="muted small"> · inactive</span>}
                  </td>
                  <td className="muted small">{p.title}</td>
                  <td className="muted small">{p.teaches}</td>
                  <td className="muted small mono">{p.rate_class}</td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </Card>

      <Card
        title="The Diary"
        actions={
          <Actions>
            <input
              type="date"
              value={from}
              onChange={(e) => setFrom(e.target.value)}
              style={{ maxWidth: 170 }}
            />
          </Actions>
        }
        flush
      >
        {diary.loading ? (
          <div style={{ padding: 16 }}>
            <Loading rows={3} />
          </div>
        ) : lessons.length === 0 ? (
          <Empty>Nothing booked on {fmtDate(from)}.</Empty>
        ) : (
          <table className="data">
            <thead>
              <tr>
                <th style={{ width: 170 }}>When</th>
                <th>Professional</th>
                <th>Student</th>
                <th style={{ width: 140 }}>Court</th>
                <th style={{ width: 120 }}>Kind</th>
                <th style={{ width: 110 }}>Status</th>
              </tr>
            </thead>
            <tbody>
              {lessons.map((l) => (
                <tr key={l.lesson_id}>
                  <td className="muted small">{dateTime(l.starts_at)}</td>
                  <td>{l.professional_name}</td>
                  <td>{l.student_name}</td>
                  <td className="muted small">
                    {l.court_name ?? <span className="muted">off court</span>}
                  </td>
                  <td className="muted small">{l.kind.replace('_', ' ')}</td>
                  <td className="muted small">{l.status}</td>
                </tr>
              ))}
            </tbody>
          </table>
        )}

        <div className="hint">
          A lesson with a court holds that court on the sheet, so the desk cannot sell it
          underneath the professional. Canceling the lesson hands the court back on its own.
        </div>
      </Card>

      {!canEdit && (
        <div className="hint">Adding a professional is a manager&apos;s job.</div>
      )}
    </>
  );
}

/* ------------------------------------------------------------------ */
/* Equipment                                                           */
/* ------------------------------------------------------------------ */

function EquipmentTab({ canEdit }: { canEdit: boolean }) {
  const data = useApi<{ equipment: Equipment[] }>('/v1/tennis/equipment');
  const [f, setF] = useState({ code: '', name: '', kind: 'ball_machine' });

  const add = useMutation(async () => {
    await api('/v1/tennis/equipment', { method: 'POST', body: f });
    setF({ code: '', name: '', kind: 'ball_machine' });
    data.reload();
  });

  const setStatus = useMutation(async (id: string, status: string) => {
    await api(`/v1/tennis/equipment/${id}`, { method: 'PATCH', body: { status } });
    data.reload();
  });

  const rows = data.data?.equipment ?? [];

  return (
    <>
      <ErrorBanner error={data.error} />
      <ErrorBanner error={add.error} />
      <ErrorBanner error={setStatus.error} />

      <Card title="What the Club Lends by the Hour" flush>
        {data.loading ? (
          <div style={{ padding: 16 }}>
            <Loading rows={3} />
          </div>
        ) : rows.length === 0 ? (
          <Empty>Nothing on the shelf yet.</Empty>
        ) : (
          <table className="data">
            <thead>
              <tr>
                <th style={{ width: 90 }}>Code</th>
                <th>What it is</th>
                <th style={{ width: 150 }}>Status</th>
                <th>Next out</th>
                <th style={{ width: 190 }} />
              </tr>
            </thead>
            <tbody>
              {rows.map((r) => (
                <tr key={r.resource_id}>
                  <td className="mono">{r.code}</td>
                  <td>
                    {r.name}
                    <span className="muted small"> · {r.kind.replace('_', ' ')}</span>
                  </td>
                  <td>
                    {r.status === 'available' ? (
                      <Status tone="good">available</Status>
                    ) : r.status === 'maintenance' ? (
                      <Status tone="warning">in the shop</Status>
                    ) : (
                      <Status tone="neutral">retired</Status>
                    )}
                  </td>
                  <td className="muted small">
                    {r.out_from ? `${dateTime(r.out_from)} — ${r.out_to ?? 'the shop'}` : '—'}
                  </td>
                  <td>
                    {canEdit && r.status !== 'retired' && (
                      <Actions>
                        <button
                          className="secondary small"
                          disabled={setStatus.busy}
                          onClick={() =>
                            setStatus.run(
                              r.resource_id,
                              r.status === 'available' ? 'maintenance' : 'available',
                            )
                          }
                        >
                          {r.status === 'available' ? 'Send to the shop' : 'Back in service'}
                        </button>
                      </Actions>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </Card>

      {canEdit && (
        <Card title="Add Something">
          <div className="grid-2">
            <label className="field">
              <span>Code</span>
              <input
                value={f.code}
                onChange={(e) => setF({ ...f, code: e.target.value })}
                placeholder="BM-1"
              />
            </label>
            <label className="field">
              <span>Name</span>
              <input
                value={f.name}
                onChange={(e) => setF({ ...f, name: e.target.value })}
                placeholder="Ball Machine 1"
              />
            </label>
            <label className="field">
              <span>What it is</span>
              <select value={f.kind} onChange={(e) => setF({ ...f, kind: e.target.value })}>
                <option value="ball_machine">Ball machine</option>
                <option value="backboard">Backboard</option>
                <option value="stringing_machine">Stringing machine</option>
                <option value="court_camera">Court camera</option>
                <option value="other">Something else</option>
              </select>
            </label>
          </div>
          <Actions>
            <button
              className="gold"
              disabled={!f.code || !f.name || add.busy}
              onClick={() => add.run()}
            >
              {add.busy ? 'Adding…' : 'Add it'}
            </button>
          </Actions>
          <div className="hint">
            The ball machine is not a court: it is carried to whichever court is free, and a
            member books both. Two members and one machine is one member, which is the rule this
            list exists to hold.
          </div>
        </Card>
      )}
    </>
  );
}
